import React, { useState } from "react";

interface DropCatchModalProps {
  onClose: () => void;
  onHaveCredentials: () => void;
}

type Step = "question" | "have-account" | "need-account";

const DropCatchModal: React.FC<DropCatchModalProps> = ({ onClose, onHaveCredentials }) => {
  const [step, setStep] = useState<Step>("question");

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/80"
        onClick={onClose}
      />

      {/* Modal */}
      <div
        className="relative w-full max-w-lg border-2 border-green-500 bg-black p-6"
        style={{
          boxShadow: "0 0 30px rgba(34, 197, 94, 0.4)",
        }}
      >
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-green-600 hover:text-green-400 text-xl"
        >
          [X]
        </button>

        {/* Step 1: Question */}
        {step === "question" && (
          <div className="space-y-6">
            <div className="text-center">
              <h2 className="text-green-500 text-lg tracking-wider mb-2">
                *** DROPCATCH API SETUP ***
              </h2>
              <div className="border-t border-green-700 my-4" />
              <p className="text-green-400 text-sm">
                DO YOU HAVE A DROPCATCH ACCOUNT?
              </p>
            </div>

            <div className="space-y-3">
              <button
                onClick={() => setStep("have-account")}
                className="w-full py-3 border-2 border-green-500 text-green-500 hover:bg-green-500 hover:text-black transition-colors font-bold tracking-wider"
              >
                [YES, I HAVE AN ACCOUNT]
              </button>
              <button
                onClick={() => setStep("need-account")}
                className="w-full py-3 border-2 border-green-700 text-green-600 hover:border-green-500 hover:text-green-500 transition-colors font-bold tracking-wider"
              >
                [NO, I NEED TO SIGN UP]
              </button>
            </div>
          </div>
        )}

        {/* Step 2: Have Account - Get API Credentials */}
        {step === "have-account" && (
          <div className="space-y-4">
            <div className="text-center">
              <h2 className="text-green-500 text-lg tracking-wider mb-2">
                *** GET YOUR API CREDENTIALS ***
              </h2>
              <div className="border-t border-green-700 my-4" />
            </div>

            <div className="text-green-400 text-sm space-y-4">
              <p className="text-green-500 font-bold">
                FOLLOW THESE STEPS TO OBTAIN YOUR API CREDENTIALS:
              </p>

              <ol className="space-y-3 list-none">
                <li className="flex gap-2">
                  <span className="text-green-600">1.</span>
                  <span>Log in to your DropCatch account at <span className="text-green-500">dropcatch.com</span></span>
                </li>
                <li className="flex gap-2">
                  <span className="text-green-600">2.</span>
                  <span>Navigate to <span className="text-green-500">Account Settings</span> → <span className="text-green-500">API Access</span></span>
                </li>
                <li className="flex gap-2">
                  <span className="text-green-600">3.</span>
                  <span>Click <span className="text-green-500">"Generate New API Key"</span></span>
                </li>
                <li className="flex gap-2">
                  <span className="text-green-600">4.</span>
                  <span>Copy your <span className="text-green-500">Client ID</span> and <span className="text-green-500">Client Secret</span></span>
                </li>
                <li className="flex gap-2">
                  <span className="text-green-600">5.</span>
                  <span>Return here and enter your credentials</span>
                </li>
              </ol>

              <div className="border-2 border-yellow-500/50 p-3 mt-4">
                <p className="text-yellow-500 text-xs">
                  [IMPORTANT] Keep your credentials secure. Never share them with anyone.
                </p>
              </div>
            </div>

            <div className="space-y-3 mt-6">
              <button
                onClick={onHaveCredentials}
                className="w-full py-3 bg-green-500 text-black font-bold tracking-wider hover:bg-green-400 transition-colors"
              >
                [I HAVE MY CREDENTIALS]
              </button>
              <button
                onClick={() => setStep("question")}
                className="w-full py-2 text-green-600 hover:text-green-400 text-sm"
              >
                [BACK]
              </button>
            </div>
          </div>
        )}

        {/* Step 3: Need Account - Sign Up for DropCatch */}
        {step === "need-account" && (
          <div className="space-y-4">
            <div className="text-center">
              <h2 className="text-green-500 text-lg tracking-wider mb-2">
                *** CREATE DROPCATCH ACCOUNT ***
              </h2>
              <div className="border-t border-green-700 my-4" />
            </div>

            <div className="text-green-400 text-sm space-y-4">
              <p>
                DropCatch is a domain auction platform. You'll need an account to:
              </p>

              <ul className="space-y-2 list-none">
                <li className="flex gap-2">
                  <span className="text-green-600">•</span>
                  <span>Place bids on expiring domains</span>
                </li>
                <li className="flex gap-2">
                  <span className="text-green-600">•</span>
                  <span>Create backorders for domains you want</span>
                </li>
                <li className="flex gap-2">
                  <span className="text-green-600">•</span>
                  <span>Access real-time auction data</span>
                </li>
              </ul>

              <div className="border-t border-green-700 my-4" />

              <p className="text-green-500 font-bold">STEPS TO GET STARTED:</p>

              <ol className="space-y-2 list-none">
                <li className="flex gap-2">
                  <span className="text-green-600">1.</span>
                  <span>Visit <span className="text-green-500">dropcatch.com/register</span></span>
                </li>
                <li className="flex gap-2">
                  <span className="text-green-600">2.</span>
                  <span>Complete the registration form</span>
                </li>
                <li className="flex gap-2">
                  <span className="text-green-600">3.</span>
                  <span>Wait for account approval (usually 1-2 business days)</span>
                </li>
                <li className="flex gap-2">
                  <span className="text-green-600">4.</span>
                  <span>Once approved, follow the API setup steps</span>
                </li>
              </ol>

              <div className="border-2 border-blue-500/50 p-3 mt-4">
                <p className="text-blue-400 text-xs">
                  [NOTE] You can still use Zero Point Domains in LIMITED MODE without DropCatch credentials. Some features will be restricted.
                </p>
              </div>
            </div>

            <div className="space-y-3 mt-6">
              <a
                href="https://www.dropcatch.com/register"
                target="_blank"
                rel="noopener noreferrer"
                className="block w-full py-3 bg-green-500 text-black font-bold tracking-wider hover:bg-green-400 transition-colors text-center"
              >
                [OPEN DROPCATCH SIGNUP]
              </a>
              <button
                onClick={onClose}
                className="w-full py-3 border-2 border-yellow-500/50 text-yellow-500 hover:border-yellow-500 transition-colors font-bold tracking-wider"
              >
                [SKIP FOR NOW - LIMITED MODE]
              </button>
              <button
                onClick={() => setStep("question")}
                className="w-full py-2 text-green-600 hover:text-green-400 text-sm"
              >
                [BACK]
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default DropCatchModal;
